//Salidas con control de stock integrado y autocompletado de usuario

import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { 
    API_URLS, 
    registrarSalida,
    obtenerProductos,
    obtenerEntradas,
    obtenerSalidas,
    obtenerMantenimientoss
} from '../../Services/servicioURL';
import { BASE_URL } from '../../Services/servicioURL';
import ResponsiveProductsTable from '../../components/contenido/TablaResponsiva';

// Interfaces consolidadas
interface Producto {
    id: number;
    producto: string;
    descripcion: string;
    cantidad: number;
    stockDisponible?: number;
    idSucursalDestino?: number;
    cveDepartamentoDestino?: string;
    nombreUsuario?: string;
    observaciones?: string;
}

interface ProductoCatalogo {
    cve: string;
    descripcion: string;
    modelo: string;
    serie: string;
    stockDisponible?: number;
}

interface StockProducto {
    cve_producto: string;
    descripcion: string;
    stock_disponible: number;
    entradaTotal: number;
    salidaTotal: number;
    mantenimientoTotal: number;
}

interface SalidaDetalle {
    folio: string;
    id: number;
    cve_producto: string;
    cantidad: number;
    id_sucursal_destino: number;
    cve_departamento_destino: string;
    nombre_usuario: string;
    observaciones: string;
}

interface SalidaType {
    folio: string;
    fecha: string;
    id_usuario_capturo: number;
    id_usuario_recibio: number;
    observaciones: string;
    folio_peticion?: number | null;
    salidaDetalle: SalidaDetalle[];
}

interface EmpleadoCatalogo {
    cveemp: string;
    nomemp: string;
    departamento: string;
    sucursal: string;
}

interface PeticionType {
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

// Interfaces para movimientos
interface EntradaDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Entrada {
    folio: string;
    fecha: string;
    entradaDetalle: EntradaDetalle[];
}

interface MantenimientoDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Mantenimiento {
    folio: string;
    mantenimientoDetalle: MantenimientoDetalle[];
}

// Funciones de API consolidadas
const apiService = {
    obtenerUsuarios: async () => {
        const response = await fetch(`${BASE_URL}/usuario/getUsuarios`);
        if (!response.ok) throw new Error('Error al obtener usuarios');
        return response.json();
    },
    obtenerEmpleados: async () => {
        const response = await fetch(API_URLS.empleados.obtener);
        if (!response.ok) throw new Error('Error al obtener empleados');
        return response.json();
    },
    obtenerProductos: async () => {
        const response = await fetch(API_URLS.productos.obtener);
        if (!response.ok) throw new Error('Error al obtener productos');
        return response.json();
    },
    obtenerSucursales: async () => {
        const response = await fetch(`${BASE_URL}/sucursal/getSucursales`);
        if (!response.ok) throw new Error('Error al obtener sucursales');
        return response.json();
    },
    obtenerDepartamentos: async () => {
        const response = await fetch(`${BASE_URL}/departamento/getDepartamentos`);
        if (!response.ok) throw new Error('Error al obtener departamentos');
        return response.json();
    },
    obtenerPeticiones: async () => {
        const response = await fetch(API_URLS.peticiones.obtener);
        if (!response.ok) throw new Error('Error al obtener peticiones');
        return response.json();
    },
    obtenerSalidas: async () => {
        const response = await fetch(`${BASE_URL}/salida/getSalidas`);
        if (!response.ok) throw new Error('Error al obtener salidas');
        return response.json();
    }
};

// Funciones para calcular stock (basadas en el componente de inventario)
const sumarMovimientosPorProducto = (
    movimientos: (Entrada | SalidaType)[]
): Map<string, number> => {
    const totalMap = new Map<string, number>();

    movimientos.forEach(mov => {
        const detalles = 'entradaDetalle' in mov ? mov.entradaDetalle : mov.salidaDetalle;

        detalles.forEach(detalle => {
            const actual = totalMap.get(detalle.cve_producto) || 0;
            totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
        });
    });

    return totalMap;
};

const sumarMantenimientosPorProducto = (mantenimientos: Mantenimiento[]): Map<string, number> => {
    const totalMap = new Map<string, number>();
    mantenimientos.forEach(mant => {
        if (Array.isArray(mant.mantenimientoDetalle)) {
            mant.mantenimientoDetalle.forEach(detalle => {
                const actual = totalMap.get(detalle.cve_producto) || 0;
                totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
            });
        }
    });
    return totalMap;
};

const calcularStockCompleto = async (): Promise<Map<string, StockProducto>> => {
    try {
        const [productos, entradas, salidas, mantenimientos] = await Promise.all([
            obtenerProductos(API_URLS.productos.obtener),
            obtenerEntradas(API_URLS.entradas.obtener),
            obtenerSalidas(API_URLS.salidas.obtener),
            obtenerMantenimientoss(API_URLS.mantenimientos.obtener)
        ]);

        const entradasTotales = sumarMovimientosPorProducto(entradas);
        const salidasTotales = sumarMovimientosPorProducto(salidas);
        const mantenimientosTotales = sumarMantenimientosPorProducto(mantenimientos);

        const stockMap = new Map<string, StockProducto>();

        productos.forEach((producto: any) => {
            const entradaTotal = entradasTotales.get(producto.cve) || 0;
            const salidaTotal = salidasTotales.get(producto.cve) || 0;
            const mantenimientoTotal = mantenimientosTotales.get(producto.cve) || 0;
            const stockDisponible = entradaTotal - salidaTotal - mantenimientoTotal;

            stockMap.set(producto.cve, {
                cve_producto: producto.cve,
                descripcion: producto.descripcion,
                stock_disponible: stockDisponible,
                entradaTotal,
                salidaTotal,
                mantenimientoTotal
            });
        });

        return stockMap;
    } catch (error) {
        console.error('Error al calcular stock:', error);
        return new Map();
    }
};

const Salidas: React.FC = () => {
    // Estados principales consolidados
    const [formData, setFormData] = useState({
        folio: '',
        fecha: new Date().toISOString().split('T')[0],
        idUsuarioCapturo: null as number | null,
        idUsuarioRecibio: null as number | null,
        observaciones: '',
        folioPeticion: null as number | null,
        tituloPeticion: ''
    });

    const [productData, setProductData] = useState({
        nombreProducto: '',
        descripcionProducto: '',
        cantidadProducto: '',
        stockDisponible: 0,
        nombreUsuario: '',
        idSucursalDestino: null as number | null,
        cveDepartamentoDestino: '',
        observacionProducto: ''
    });

    const [catalogos, setCatalogos] = useState({
        usuarios: [] as any[],
        productos: [] as ProductoCatalogo[],
        empleados: [] as EmpleadoCatalogo[],
        sucursales: [] as any[],
        departamentos: [] as any[],
        peticiones: [] as PeticionType[],
        salidas: [] as SalidaType[]
    });

    const [filteredData, setFilteredData] = useState({
        productos: [] as ProductoCatalogo[],
        empleados: [] as EmpleadoCatalogo[],
        peticiones: [] as PeticionType[],
        salidas: [] as SalidaType[]
    });

    const [searchTerms, setSearchTerms] = useState({
        producto: '',
        empleado: '',
        peticion: '',
        salida: ''
    });

    const [modals, setModals] = useState({
        productos: false,
        empleados: false,
        peticiones: false,
        consultaSalidas: false
    });

    const [appState, setAppState] = useState({
        loading: true,
        mode: 'view' as 'view' | 'new' | 'edit',
        editar: false,
        selectedProductIndex: null as number | null,
        isFechaDisabled: true,
        loadingStock: false
    });

    const [products, setProducts] = useState<Producto[]>([]);
    const [nextId, setNextId] = useState<number>(1);
    const [stockMap, setStockMap] = useState<Map<string, StockProducto>>(new Map());
    const toast = useRef<Toast>(null);

    // Función para mostrar mensajes
    const showMessage = useCallback((severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    }, []);

    // NUEVA: Función para buscar empleado por código y autocompletar
    const buscarEmpleadoPorCodigo = useCallback(async (codigo: string) => {
        if (!codigo.trim()) return;

        try {
            // Si no tenemos empleados cargados, cargarlos
            if (catalogos.empleados.length === 0) {
                const empleados = await apiService.obtenerEmpleados();
                setCatalogos(prev => ({ ...prev, empleados }));
                
                // Buscar en los empleados recién cargados
                const empleadoEncontrado = empleados.find((emp: EmpleadoCatalogo) => 
                    emp.cveemp.toLowerCase() === codigo.toLowerCase()
                );
                
                if (empleadoEncontrado) {
                    setProductData(prev => ({ ...prev, nombreUsuario: empleadoEncontrado.nomemp }));
                    showMessage('success', 'Empleado encontrado', `Se seleccionó: ${empleadoEncontrado.nomemp} (${empleadoEncontrado.cveemp})`);
                } else {
                    showMessage('warn', 'Empleado no encontrado', `No se encontró ningún empleado con el código: ${codigo}`);
                    // Mantener el código ingresado para que el usuario pueda corregirlo
                }
            } else {
                // Buscar en los empleados ya cargados
                const empleadoEncontrado = catalogos.empleados.find(emp => 
                    emp.cveemp.toLowerCase() === codigo.toLowerCase()
                );
                
                if (empleadoEncontrado) {
                    setProductData(prev => ({ ...prev, nombreUsuario: empleadoEncontrado.nomemp }));
                    showMessage('success', 'Empleado encontrado', `Se seleccionó: ${empleadoEncontrado.nomemp} (${empleadoEncontrado.cveemp})`);
                } else {
                    showMessage('warn', 'Empleado no encontrado', `No se encontró ningún empleado con el código: ${codigo}`);
                    // Mantener el código ingresado para que el usuario pueda corregirlo
                }
            }
        } catch (error) {
            console.error('Error al buscar empleado:', error);
            showMessage('error', 'Error', 'No se pudo buscar el empleado');
        }
    }, [catalogos.empleados, showMessage]);

    // NUEVA: Función para manejar el evento de tecla en el campo de usuario
    const handleUsuarioKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const codigo = productData.nombreUsuario.trim();
            if (codigo) {
                buscarEmpleadoPorCodigo(codigo);
            }
        }
    }, [productData.nombreUsuario, buscarEmpleadoPorCodigo]);

    // Función para obtener stock de un producto específico
    const obtenerStockProducto = useCallback((cveProducto: string): number => {
        const stockInfo = stockMap.get(cveProducto);
        return stockInfo?.stock_disponible || 0;
    }, [stockMap]);

    // Función para cargar stock de todos los productos
    const cargarStockProductos = useCallback(async () => {
        try {
            setAppState(prev => ({ ...prev, loadingStock: true }));
            const nuevoStockMap = await calcularStockCompleto();
            setStockMap(nuevoStockMap);
            
            // Actualizar productos del catálogo con stock
            setCatalogos(prev => ({
                ...prev,
                productos: prev.productos.map(producto => ({
                    ...producto,
                    stockDisponible: nuevoStockMap.get(producto.cve)?.stock_disponible || 0
                }))
            }));

            // Actualizar productos filtrados
            setFilteredData(prev => ({
                ...prev,
                productos: prev.productos.map(producto => ({
                    ...producto,
                    stockDisponible: nuevoStockMap.get(producto.cve)?.stock_disponible || 0
                }))
            }));

        } catch (error) {
            console.error('Error al cargar stock:', error);
            showMessage('warn', 'Advertencia', 'No se pudo cargar la información de stock');
        } finally {
            setAppState(prev => ({ ...prev, loadingStock: false }));
        }
    }, [showMessage]);

    // Cargar datos iniciales
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setAppState(prev => ({ ...prev, loading: true }));
                const [usuarios, productos, sucursales, departamentos] = await Promise.all([
                    apiService.obtenerUsuarios(),
                    apiService.obtenerProductos(),
                    apiService.obtenerSucursales(),
                    apiService.obtenerDepartamentos()
                ]);

                setCatalogos(prev => ({ ...prev, usuarios, productos, sucursales, departamentos }));
                setFilteredData(prev => ({ ...prev, productos }));

                // Establecer usuario capturó desde localStorage
                const idUsuario = localStorage.getItem('idUsuario');
                if (idUsuario) {
                    setFormData(prev => ({ ...prev, idUsuarioCapturo: parseInt(idUsuario) }));
                }

                // Cargar stock después de cargar productos
                await cargarStockProductos();
            } catch (error) {
                console.error('Error al cargar datos:', error);
                showMessage('error', 'Error', 'No se pudieron cargar los datos iniciales');
            } finally {
                setAppState(prev => ({ ...prev, loading: false }));
            }
        };
        loadInitialData();
    }, [showMessage, cargarStockProductos]);

    // Obtener folio automático para nuevas salidas
    useEffect(() => {
        const cargarFolioAutomatico = async () => {
            if (appState.mode === 'new') {
                try {
                    const salidas = await apiService.obtenerSalidas();
                    const folios = salidas.map((s: any) => parseInt(s.folio)).filter(Number);
                    const ultimoFolio = folios.length ? Math.max(...folios) : 0;
                    setFormData(prev => ({ ...prev, folio: (ultimoFolio + 1).toString() }));
                } catch (error) {
                    console.error('Error al cargar folio:', error);
                    setFormData(prev => ({ ...prev, folio: '1' }));
                }
            }
        };
        cargarFolioAutomatico();
    }, [appState.mode]);

    // Filtros consolidados
    useEffect(() => {
        const applyFilters = () => {
            // Filtrar productos
            if (searchTerms.producto) {
                const filtered = catalogos.productos.filter(p =>
                    Object.values(p).some(val => 
                        val?.toString().toLowerCase().includes(searchTerms.producto.toLowerCase())
                    )
                );
                setFilteredData(prev => ({ ...prev, productos: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, productos: catalogos.productos }));
            }

            // Filtrar empleados
            if (searchTerms.empleado) {
                const filtered = catalogos.empleados.filter(e =>
                    Object.values(e).some(val => 
                        val?.toString().toLowerCase().includes(searchTerms.empleado.toLowerCase())
                    )
                );
                setFilteredData(prev => ({ ...prev, empleados: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, empleados: catalogos.empleados }));
            }

            // Filtrar peticiones
            if (searchTerms.peticion) {
                const filtered = catalogos.peticiones.filter(p =>
                    p.folio.toString().includes(searchTerms.peticion) ||
                    p.titulo.toLowerCase().includes(searchTerms.peticion.toLowerCase()) ||
                    p.empleado?.nomemp?.toLowerCase().includes(searchTerms.peticion.toLowerCase())
                );
                setFilteredData(prev => ({ ...prev, peticiones: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, peticiones: catalogos.peticiones }));
            }

            // Filtrar salidas
            if (searchTerms.salida) {
                const filtered = catalogos.salidas.filter(s =>
                    s.folio.toLowerCase().includes(searchTerms.salida.toLowerCase()) ||
                    s.observaciones.toLowerCase().includes(searchTerms.salida.toLowerCase())
                );
                setFilteredData(prev => ({ ...prev, salidas: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, salidas: catalogos.salidas }));
            }
        };

        applyFilters();
    }, [searchTerms, catalogos]);

    // Funciones de manejo de modales
    const openModal = useCallback(async (modalType: keyof typeof modals) => {
        try {
            setAppState(prev => ({ ...prev, loading: true }));
            
            switch (modalType) {
                case 'productos':
                    if (catalogos.productos.length === 0) {
                        const productos = await apiService.obtenerProductos();
                        setCatalogos(prev => ({ ...prev, productos }));
                    }
                    // Actualizar stock al abrir modal de productos
                    await cargarStockProductos();
                    break;
                case 'empleados':
                    if (catalogos.empleados.length === 0) {
                        const empleados = await apiService.obtenerEmpleados();
                        setCatalogos(prev => ({ ...prev, empleados }));
                    }
                    break;
                case 'peticiones':
                    const peticiones = await apiService.obtenerPeticiones();
                    const peticionesListas = peticiones.filter((p: PeticionType) => 
                        p.estatus.toUpperCase() === 'TRABAJANDO'
                    );
                    setCatalogos(prev => ({ ...prev, peticiones: peticionesListas }));
                    break;
                case 'consultaSalidas':
                    const salidas = await apiService.obtenerSalidas();
                    setCatalogos(prev => ({ ...prev, salidas }));
                    break;
            }
            
            setModals(prev => ({ ...prev, [modalType]: true }));
            setSearchTerms(prev => ({ ...prev, [modalType === 'consultaSalidas' ? 'salida' : modalType.slice(0, -1)]: '' }));
        } catch (error) {
            showMessage('error', 'Error', `No se pudieron cargar los ${modalType}`);
        } finally {
            setAppState(prev => ({ ...prev, loading: false }));
        }
    }, [catalogos, showMessage, cargarStockProductos]);

    const closeModal = useCallback((modalType: keyof typeof modals) => {
        setModals(prev => ({ ...prev, [modalType]: false }));
    }, []);

    // Funciones de selección
    const seleccionarProducto = useCallback((producto: ProductoCatalogo) => {
        const stockActual = obtenerStockProducto(producto.cve);
        
        setProductData(prev => ({
            ...prev,
            nombreProducto: producto.cve,
            descripcionProducto: producto.descripcion,
            cantidadProducto: '1',
            stockDisponible: stockActual
        }));
        closeModal('productos');
        
        if (stockActual <= 0) {
            showMessage('warn', 'Sin stock', `El producto ${producto.descripcion} no tiene stock disponible`);
        } else {
            showMessage('info', 'Producto seleccionado', `${producto.descripcion} - Stock disponible: ${stockActual}`);
        }
    }, [closeModal, showMessage, obtenerStockProducto]);

    const seleccionarEmpleado = useCallback((empleado: EmpleadoCatalogo) => {
        setProductData(prev => ({ ...prev, nombreUsuario: empleado.nomemp }));
        closeModal('empleados');
        showMessage('info', 'Empleado seleccionado', `Se ha seleccionado: ${empleado.nomemp}`);
    }, [closeModal, showMessage]);

    const seleccionarPeticion = useCallback((peticion: PeticionType) => {
        setFormData(prev => ({
            ...prev,
            folioPeticion: peticion.folio,
            tituloPeticion: peticion.titulo
        }));
        closeModal('peticiones');
        showMessage('info', 'Petición seleccionada', `Se ha seleccionado la petición: ${peticion.folio}`);
    }, [closeModal, showMessage]);

    const seleccionarSalida = useCallback((salida: SalidaType) => {
        setFormData({
            folio: salida.folio,
            fecha: salida.fecha,
            idUsuarioCapturo: salida.id_usuario_capturo,
            idUsuarioRecibio: salida.id_usuario_recibio,
            observaciones: salida.observaciones,
            folioPeticion: salida.folio_peticion || null,
            tituloPeticion: salida.folio_peticion ? 
                catalogos.peticiones.find(p => p.folio === salida.folio_peticion)?.titulo || 'Título no encontrado' : ''
        });

        if (Array.isArray(salida.salidaDetalle)) {
            const productosConDescripcion = salida.salidaDetalle.map(d => {
                const stockActual = obtenerStockProducto(d.cve_producto);
                return {
                    id: d.id,
                    producto: d.cve_producto,
                    descripcion: stockMap.get(d.cve_producto)?.descripcion || 'Descripción no encontrada',
                    cantidad: d.cantidad,
                    stockDisponible: stockActual,
                    idSucursalDestino: d.id_sucursal_destino,
                    cveDepartamentoDestino: d.cve_departamento_destino,
                    nombreUsuario: d.nombre_usuario,
                    observaciones: d.observaciones
                };
            });
            setProducts(productosConDescripcion);
        }
        
        setAppState(prev => ({ ...prev, mode: 'view' }));
        closeModal('consultaSalidas');
    }, [catalogos, closeModal, obtenerStockProducto, stockMap]);

    // Funciones de productos
    const limpiarCamposProducto = useCallback(() => {
        setProductData({
            nombreProducto: '',
            descripcionProducto: '',
            cantidadProducto: '',
            stockDisponible: 0,
            nombreUsuario: '',
            idSucursalDestino: null,
            cveDepartamentoDestino: '',
            observacionProducto: ''
        });
        setAppState(prev => ({ ...prev, editar: false, selectedProductIndex: null }));
    }, []);

    const agregarProducto = useCallback(() => {
        const { nombreProducto, descripcionProducto, cantidadProducto, stockDisponible, nombreUsuario, idSucursalDestino, cveDepartamentoDestino, observacionProducto } = productData;
        
        if (!nombreProducto || !descripcionProducto || !cantidadProducto || !nombreUsuario || !idSucursalDestino || !cveDepartamentoDestino) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos con * son obligatorios');
            return;
        }

        const cantidadSolicitada = parseInt(cantidadProducto);
        
        // Validación de stock disponible
        if (cantidadSolicitada > stockDisponible) {
            showMessage('error', 'Stock insuficiente', 
                `No hay suficiente stock. Disponible: ${stockDisponible}, Solicitado: ${cantidadSolicitada}`);
            return;
        }

        // Verificar si el producto ya está en la lista
        const productoExistente = products.find(p => p.producto === nombreProducto);
        if (productoExistente) {
            const cantidadTotal = productoExistente.cantidad + cantidadSolicitada;
            if (cantidadTotal > stockDisponible) {
                showMessage('error', 'Stock insuficiente', 
                    `El producto ya está agregado con cantidad ${productoExistente.cantidad}. Total sería: ${cantidadTotal}, pero solo hay ${stockDisponible} disponibles`);
                return;
            }
        }

        const newProduct: Producto = {
            id: nextId,
            producto: nombreProducto,
            descripcion: descripcionProducto,
            cantidad: cantidadSolicitada,
            stockDisponible,
            nombreUsuario,
            idSucursalDestino,
            cveDepartamentoDestino,
            observaciones: observacionProducto
        };

        setProducts(prev => [...prev, newProduct]);
        setNextId(prev => prev + 1);
        limpiarCamposProducto();
        showMessage('success', '¡Producto agregado!', 
            `Producto agregado correctamente. Stock restante: ${stockDisponible - cantidadSolicitada}`);
    }, [productData, nextId, products, limpiarCamposProducto, showMessage]);

    const actualizarProducto = useCallback(() => {
        const { nombreProducto, descripcionProducto, cantidadProducto, stockDisponible, nombreUsuario, idSucursalDestino, cveDepartamentoDestino, observacionProducto } = productData;
        
        if (!nombreProducto || !descripcionProducto || !cantidadProducto || !nombreUsuario || !idSucursalDestino || !cveDepartamentoDestino || appState.selectedProductIndex === null) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos con * son obligatorios');
            return;
        }

        const cantidadSolicitada = parseInt(cantidadProducto);
        
        // Validación de stock disponible
        if (cantidadSolicitada > stockDisponible) {
            showMessage('error', 'Stock insuficiente', 
                `No hay suficiente stock. Disponible: ${stockDisponible}, Solicitado: ${cantidadSolicitada}`);
            return;
        }

        const updatedProduct: Producto = {
            id: products[appState.selectedProductIndex].id,
            producto: nombreProducto,
            descripcion: descripcionProducto,
            cantidad: cantidadSolicitada,
            stockDisponible,
            nombreUsuario,
            idSucursalDestino,
            cveDepartamentoDestino,
            observaciones: observacionProducto
        };

        setProducts(prev => {
            const updated = [...prev];
            updated[appState.selectedProductIndex!] = updatedProduct;
            return updated;
        });
        
        limpiarCamposProducto();
        showMessage('success', '¡Producto actualizado!', 'Los cambios se guardaron correctamente');
    }, [productData, products, appState.selectedProductIndex, limpiarCamposProducto, showMessage]);

    const eliminarProducto = useCallback((index: number) => {
        const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar este producto?\n\nEsta acción no se puede deshacer.');
        if (confirmacion) {
            setProducts(prev => prev.filter((_, i) => i !== index));
            showMessage('success', '¡Producto eliminado!', 'El producto se eliminó de la lista');
        }
    }, [showMessage]);

    const seleccionarProductoParaEditar = useCallback((product: Producto, index: number) => {
        const stockActual = obtenerStockProducto(product.producto);
        
        setProductData({
            nombreProducto: product.producto,
            descripcionProducto: product.descripcion,
            cantidadProducto: product.cantidad.toString(),
            stockDisponible: stockActual,
            nombreUsuario: product.nombreUsuario || '',
            idSucursalDestino: product.idSucursalDestino || null,
            cveDepartamentoDestino: product.cveDepartamentoDestino || '',
            observacionProducto: product.observaciones || ''
        });
        setAppState(prev => ({ ...prev, editar: true, selectedProductIndex: index }));
        showMessage('info', 'Producto seleccionado', `Editando producto - Stock disponible: ${stockActual}`);
    }, [showMessage, obtenerStockProducto]);

    // Funciones principales
    const limpiarFormulario = useCallback(() => {
        setFormData({
            folio: '',
            fecha: new Date().toISOString().split('T')[0],
            idUsuarioCapturo: parseInt(localStorage.getItem('idUsuario') || '0'),
            idUsuarioRecibio: null,
            observaciones: '',
            folioPeticion: null,
            tituloPeticion: ''
        });
        setProducts([]);
        limpiarCamposProducto();
    }, [limpiarCamposProducto]);

    const handleNuevo = useCallback(() => {
        limpiarFormulario();
        setAppState(prev => ({ ...prev, mode: 'new', isFechaDisabled: false }));
    }, [limpiarFormulario]);

    const handleEditar = useCallback(() => {
        if (!formData.folio) {
            showMessage('warn', 'Advertencia', 'Seleccione una salida para editar');
            return;
        }
        setAppState(prev => ({ ...prev, mode: 'edit', isFechaDisabled: false }));
    }, [formData.folio, showMessage]);

    const handleEliminar = useCallback(async () => {
        if (!formData.folio) {
            showMessage('warn', 'Advertencia', 'Seleccione una salida para eliminar');
            return;
        }
        
        const confirmacion = window.confirm(`⚠️ ¿Está seguro de eliminar la salida con folio: ${formData.folio}?\n\nEsta acción no se puede deshacer.`);
        if (confirmacion) {
            try {
                const response = await fetch(`${BASE_URL}/salida/eliminarSalida/${formData.folio}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' }
                });
                
                if (!response.ok) throw new Error('Error al eliminar la salida');
                
                showMessage('success', '¡Salida eliminada!', 'La salida se eliminó correctamente');
                limpiarFormulario();
                // Actualizar stock después de eliminar
                await cargarStockProductos();
            } catch (error) {
                console.error('Error al eliminar:', error);
                showMessage('error', 'Error', 'No se pudo eliminar la salida');
            }
        }
    }, [formData.folio, limpiarFormulario, showMessage, cargarStockProductos]);

    const handleCancelar = useCallback(() => {
        setAppState(prev => ({ ...prev, mode: 'view', isFechaDisabled: true }));
        limpiarFormulario();
    }, [limpiarFormulario]);

    const handleGuardar = useCallback(async () => {
        const { folio, fecha, idUsuarioCapturo, idUsuarioRecibio, observaciones, folioPeticion } = formData;
        
        if (!folio || !fecha || !idUsuarioCapturo || !idUsuarioRecibio) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos marcados con * son obligatorios');
            return;
        }
        
        if (products.length === 0) {
            showMessage('warn', 'Sin productos', 'Debe agregar al menos un producto a la salida');
            return;
        }

        // Validación de stock antes de guardar
        for (const producto of products) {
            const stockActual = obtenerStockProducto(producto.producto);
            if (producto.cantidad > stockActual) {
                showMessage('error', 'Stock insuficiente', 
                    `El producto ${producto.descripcion} no tiene suficiente stock. Disponible: ${stockActual}, Requerido: ${producto.cantidad}`);
                return;
            }
        }

        const salidaDetalle = products.map((producto, index) => ({
            folio,
            id: producto.id || index + 1,
            cve_producto: producto.producto,
            cantidad: producto.cantidad,
            id_sucursal_destino: producto.idSucursalDestino,
            cve_departamento_destino: producto.cveDepartamentoDestino,
            nombre_usuario: producto.nombreUsuario,
            observaciones: producto.observaciones
        }));

        const payload = {
            folio, fecha, id_usuario_capturo: idUsuarioCapturo, id_usuario_recibio: idUsuarioRecibio,
            observaciones, folio_peticion: folioPeticion, salidaDetalle
        };

        try {
            await registrarSalida(API_URLS.salidas.registrar, payload);
            const mensaje = folioPeticion 
                ? `Salida ${appState.mode === 'new' ? 'registrada' : 'actualizada'} exitosamente (Petición: ${folioPeticion})`
                : `Salida ${appState.mode === 'new' ? 'registrada' : 'actualizada'} exitosamente`;
            
            showMessage('success', appState.mode === 'new' ? '¡Salida registrada!' : '¡Salida actualizada!', mensaje);
            limpiarFormulario();
            setAppState(prev => ({ ...prev, mode: 'view', isFechaDisabled: true }));
            
            // Actualizar stock después de guardar
            await cargarStockProductos();
        } catch (error: any) {
            console.error('Error al guardar:', error);
            showMessage('error', 'Error al guardar', error.message || 'No se pudo guardar la salida');
        }
    }, [formData, products, appState.mode, limpiarFormulario, showMessage, obtenerStockProducto, cargarStockProductos]);

    // Función para obtener información del modo actual
    const getModeIndicator = () => {
        switch (appState.mode) {
            case 'new':
                return { text: 'NUEVA SALIDA', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO SALIDA', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    // Componente para mostrar stock en la tabla de productos del modal
    const stockBodyTemplate = (rowData: ProductoCatalogo) => {
        const stock = rowData.stockDisponible || 0;
        return (
            <div style={{ textAlign: 'center' }}>
                <Badge 
                    value={stock} 
                    severity={stock > 10 ? 'success' : stock > 0 ? 'warning' : 'danger'}
                    style={{ fontSize: '0.8rem' }}
                />
            </div>
        );
    };

    // Componentes de renderizado para tabla de productos
    const actionBodyTemplate = (rowData: Producto, options: any) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button
                icon="pi pi-pencil"
                size="small"
                severity="warning"
                rounded
                onClick={() => seleccionarProductoParaEditar(rowData, options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Editar producto"
            />
            <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                rounded
                onClick={() => eliminarProducto(options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Eliminar producto"
            />
        </div>
    );

    const cantidadBodyTemplate = (rowData: Producto) => (
        <div style={{ textAlign: 'center' }}>
            <Badge value={rowData.cantidad} severity="info" />
            {rowData.stockDisponible !== undefined && (
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
                    Stock: {rowData.stockDisponible}
                </div>
            )}
        </div>
    );

    // Componentes de renderizado para tabla de salidas
    const fechaSalidaBodyTemplate = (rowData: SalidaType) => {
        const fecha = new Date(rowData.fecha);
        return (
            <div style={{ textAlign: 'center' }}>
                <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                }}>
                    {fecha.toLocaleDateString('es-ES')}
                </span>
            </div>
        );
    };

    const peticionSalidaBodyTemplate = (rowData: SalidaType) => (
        <div style={{ textAlign: 'center' }}>
            {rowData.folio_peticion ? (
                <Badge 
                    value={`#${rowData.folio_peticion}`} 
                    severity="success"
                    style={{ fontSize: '0.8rem' }}
                />
            ) : (
                <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontStyle: 'italic'
                }}>
                    Sin petición
                </span>
            )}
        </div>
    );

    const observacionesSalidaBodyTemplate = (rowData: SalidaType) => (
        <div style={{ maxWidth: '200px' }}>
            <span style={{
                fontSize: '0.85rem',
                color: rowData.observaciones ? '#374151' : '#9ca3af',
                fontStyle: rowData.observaciones ? 'normal' : 'italic',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }} title={rowData.observaciones || 'Sin observaciones'}>
                {rowData.observaciones || 'Sin observaciones'}
            </span>
        </div>
    );

    const accionesSalidaBodyTemplate = (rowData: SalidaType) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button
                icon="pi pi-eye"
                size="small"
                severity="info"
                rounded
                onClick={() => seleccionarSalida(rowData)}
                tooltip="Ver detalles"
                style={{ height: '32px', width: '32px' }}
            />
        </div>
    );

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* MODAL - Catálogo de Productos con stock */}
            {modals.productos && (
                <div className="overlay" onClick={() => closeModal('productos')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '800px', width: '90%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📦 Catálogo de Productos
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('productos')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '40px', paddingLeft: '40px',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar por clave o descripción..."
                                    value={searchTerms.producto}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, producto: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div style={{
                            marginBottom: '15px', fontSize: '0.9em', color: '#6b7280',
                            padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📊 Mostrando <strong>{filteredData.productos.length}</strong> de <strong>{catalogos.productos.length}</strong> productos
                            {appState.loadingStock && <span style={{ marginLeft: '10px' }}>🔄 Actualizando stock...</span>}
                        </div>

                        {/* Contenedor con scroll para la tabla */}
                        <div style={{ 
                            flex: 1,
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div style={{
                                overflowY: 'auto',
                                maxHeight: '400px'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>🔑 Clave</th>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>📝 Descripción</th>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'center', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb', width: '100px'
                                            }}>📊 Stock</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appState.loading ? (
                                            <tr>
                                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    🔄 Cargando productos...
                                                </td>
                                            </tr>
                                        ) : filteredData.productos.length > 0 ? (
                                            filteredData.productos.map((producto, index) => {
                                                const stock = producto.stockDisponible || 0;
                                                const stockColor = stock > 10 ? '#10b981' : stock > 0 ? '#f59e0b' : '#ef4444';
                                                return (
                                                    <tr
                                                        key={index}
                                                        onClick={() => seleccionarProducto(producto)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                    >
                                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.cve}</td>
                                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{producto.descripcion}</td>
                                                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                                                            <span style={{
                                                                padding: '4px 8px',
                                                                backgroundColor: stockColor,
                                                                color: 'white',
                                                                borderRadius: '12px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '600'
                                                            }}>
                                                                {stock}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No se encontraron productos
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Leyenda de colores para stock */}
                        <div style={{
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '20px',
                            fontSize: '0.8rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '50%' }}></span>
                                <span>Stock alto (10)</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '12px', height: '12px', backgroundColor: '#f59e0b', borderRadius: '50%' }}></span>
                                <span>Stock bajo (1-10)</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '12px', height: '12px', backgroundColor: '#ef4444', borderRadius: '50%' }}></span>
                                <span>Sin stock (0)</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Seleccionar Empleado con scroll */}
            {modals.empleados && (
                <div className="overlay" onClick={() => closeModal('empleados')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '600px', width: '90%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                👥 Seleccionar Empleado
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('empleados')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '40px', paddingLeft: '40px',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar empleado..."
                                    value={searchTerms.empleado}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, empleado: e.target.value }))}
                                />
                            </div>
                        </div>

                        {/* Contenedor con scroll para la tabla */}
                        <div style={{ 
                            flex: 1,
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div style={{
                                overflowY: 'auto',
                                maxHeight: '400px'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>🔑 Clave</th>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>👤 Nombre</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.empleados.length > 0 ? (
                                            filteredData.empleados.map((empleado, index) => (
                                                <tr
                                                    key={index}
                                                    onClick={() => seleccionarEmpleado(empleado)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                >
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{empleado.cveemp}</td>
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{empleado.nomemp}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={2} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No hay resultados
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Seleccionar Petición con scroll */}
            {modals.peticiones && (
                <div className="overlay" onClick={() => closeModal('peticiones')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '600px', width: '90%', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📋 Seleccionar Petición
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('peticiones')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '40px', paddingLeft: '40px',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar petición..."
                                    value={searchTerms.peticion}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, peticion: e.target.value }))}
                                />
                            </div>
                        </div>

                        {/* Contenedor con scroll para la tabla */}
                        <div style={{ 
                            flex: 1,
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div style={{
                                overflowY: 'auto',
                                maxHeight: '400px'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>📄 Folio</th>
                                            <th style={{
                                                padding: '12px 16px', textAlign: 'left', fontWeight: '600',
                                                color: '#374151', borderBottom: '2px solid #e5e7eb'
                                            }}>📝 Título</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.peticiones.length > 0 ? (
                                            filteredData.peticiones.map((peticion, index) => (
                                                <tr
                                                    key={index}
                                                    onClick={() => seleccionarPeticion(peticion)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                >
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{peticion.folio}</td>
                                                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{peticion.titulo}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={2} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No hay resultados
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Consultar Salidas MEJORADO con DataTable */}
            {modals.consultaSalidas && (
                <div className="overlay" onClick={() => closeModal('consultaSalidas')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '1200px', width: '95%', maxHeight: '85vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📤 Consultar Salidas
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('consultaSalidas')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '40px', paddingLeft: '40px',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar por folio u observaciones..."
                                    value={searchTerms.salida}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, salida: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div style={{
                            marginBottom: '15px', fontSize: '0.9em', color: '#6b7280',
                            padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📊 Mostrando <strong>{filteredData.salidas.length}</strong> de <strong>{catalogos.salidas.length}</strong> salidas
                        </div>

                        <div style={{
                            flex: 1, overflow: 'hidden', border: '1px solid #e5e7eb', borderRadius: '8px'
                        }}>
                            <DataTable
                                value={filteredData.salidas}
                                responsiveLayout="scroll"
                                stripedRows
                                size="small"
                                emptyMessage={
                                    <div style={{
                                        textAlign: 'center', padding: '40px 20px',
                                        color: '#6b7280', fontSize: '1rem'
                                    }}>
                                        📤 No hay salidas registradas
                                        <br />
                                        <span style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                                            Las salidas aparecerán aquí una vez que sean registradas
                                        </span>
                                    </div>
                                }
                                className="p-datatable-sm"
                                style={{ fontSize: '0.9rem' }}
                                scrollable
                                scrollHeight="calc(85vh - 250px)"
                                loading={appState.loading}
                                loadingIcon="pi pi-spin pi-spinner"
                                selectionMode="single"
                                onSelectionChange={(e) => seleccionarSalida(e.value)}
                                metaKeySelection={false}
                                dataKey="folio"
                            >
                                <Column 
                                    field="folio" 
                                    header="📄 Folio" 
                                    headerStyle={{ 
                                        width: '100px', textAlign: 'center', fontWeight: 'bold',
                                        backgroundColor: '#f8fafc', color: '#374151'
                                    }} 
                                    bodyStyle={{ 
                                        textAlign: 'center', padding: '8px',
                                        fontWeight: '600', color: '#1e40af'
                                    }}
                                    sortable
                                />
                                <Column 
                                    field="fecha" 
                                    header="📅 Fecha" 
                                    headerStyle={{ 
                                        width: '120px', textAlign: 'center', fontWeight: 'bold',
                                        backgroundColor: '#f8fafc', color: '#374151'
                                    }} 
                                    body={fechaSalidaBodyTemplate}
                                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                                    sortable
                                />
                                <Column 
                                    field="observaciones" 
                                    header="💬 Observaciones" 
                                    headerStyle={{ 
                                        minWidth: '200px', fontWeight: 'bold',
                                        backgroundColor: '#f8fafc', color: '#374151'
                                    }} 
                                    body={observacionesSalidaBodyTemplate}
                                    bodyStyle={{ padding: '8px' }}
                                />
                                <Column 
                                    field="folio_peticion" 
                                    header="📋 Petición" 
                                    headerStyle={{ 
                                        width: '120px', textAlign: 'center', fontWeight: 'bold',
                                        backgroundColor: '#f8fafc', color: '#374151'
                                    }} 
                                    body={peticionSalidaBodyTemplate}
                                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                                    sortable
                                />
                                <Column 
                                    body={accionesSalidaBodyTemplate} 
                                    header="⚙️ Acciones" 
                                    headerStyle={{ 
                                        width: '80px', textAlign: 'center', fontWeight: 'bold',
                                        backgroundColor: '#f8fafc', color: '#374151'
                                    }} 
                                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Resumen de salidas */}
                        {filteredData.salidas.length > 0 && (
                            <div style={{
                                marginTop: '16px',
                                padding: '12px 16px',
                                backgroundColor: '#f0f9ff',
                                border: '1px solid #bae6fd',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{ color: '#0369a1', fontWeight: '600' }}>
                                    📊 Total de salidas: {filteredData.salidas.length}
                                </span>
                                <span style={{ color: '#0369a1', fontWeight: '600' }}>
                                    📋 Con petición: {filteredData.salidas.filter(s => s.folio_peticion).length}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
{/* Contenido principal */}
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed',
                minHeight: '100vh', position: 'relative', padding: '20px'
            }}>
                {/* Header principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px', padding: '24px', marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{
                            margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700',
                            display: 'flex', alignItems: 'center', gap: '12px'
                        }}>
                            📤 Gestión de Salidas
                        </h1>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 16px', backgroundColor: modeInfo.color,
                            color: 'white', borderRadius: '20px',
                            fontSize: '0.85rem', fontWeight: '600'
                        }}>
                            <i className={`pi ${modeInfo.icon}`}></i>
                            {modeInfo.text}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Formulario principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px', padding: '24px', marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Botones de acción reorganizados en una sola línea */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {appState.mode === 'view' ? (
                                <>
                                    <Button
                                        icon="pi pi-search"
                                        label="Consultar Salidas"
                                        className="p-button-info p-button-raised"
                                        onClick={() => openModal('consultaSalidas')}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '160px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-plus"
                                        label="Nueva Salida"
                                        className="p-button-success p-button-raised"
                                        onClick={handleNuevo}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '140px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        label="Editar"
                                        className="p-button-warning p-button-raised"
                                        onClick={handleEditar}
                                        disabled={!formData.folio}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px',
                                            opacity: !formData.folio ? 0.5 : 1
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        label="Eliminar"
                                        className="p-button-danger p-button-raised"
                                        onClick={handleEliminar}
                                        disabled={!formData.folio}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px',
                                            opacity: !formData.folio ? 0.5 : 1
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        icon="pi pi-save"
                                        label="Guardar"
                                        className="p-button-success p-button-raised"
                                        onClick={handleGuardar}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        label="Cancelar"
                                        className="p-button-secondary p-button-raised"
                                        onClick={handleCancelar}
                                        style={{
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px'
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Formulario de datos generales */}
                    <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '20px',
                            marginBottom: '24px'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📄 Folio <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    value={formData.folio}
                                    onChange={(e) => setFormData(prev => ({ ...prev, folio: e.target.value }))}
                                    disabled
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px', border: '2px solid #e5e7eb',
                                        borderRadius: '8px', backgroundColor: '#f9fafb'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📅 Fecha <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText
                                    type="date"
                                    value={formData.fecha}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                                    disabled={appState.isFechaDisabled}
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px',
                                        border: `2px solid ${appState.isFechaDisabled ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: appState.isFechaDisabled ? '#f9fafb' : '#ffffff'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    👤 Usuario Capturó <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                {appState.loading ? (
                                    <div style={{
                                        padding: '12px', color: '#6b7280', fontStyle: 'italic',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb'
                                    }}>
                                        🔄 Cargando usuarios...
                                    </div>
                                ) : (
                                    <Dropdown
                                        value={formData.idUsuarioCapturo}
                                        options={catalogos.usuarios}
                                        optionLabel="nombre"
                                        optionValue="id"
                                        onChange={(e) => setFormData(prev => ({ ...prev, idUsuarioCapturo: e.value }))}
                                        disabled
                                        placeholder="Usuario que captura"
                                        style={{
                                            width: '100%', height: '40px',
                                            border: '2px solid #e5e7eb', borderRadius: '8px'
                                        }}
                                    />
                                )}
                            </div>

                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    👥 Usuario Recibió <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <Dropdown
                                    value={formData.idUsuarioRecibio}
                                    options={catalogos.usuarios}
                                    optionLabel="nombre"
                                    optionValue="id"
                                    onChange={(e) => setFormData(prev => ({ ...prev, idUsuarioRecibio: e.value }))}
                                    disabled={appState.mode === 'view'}
                                    placeholder="Seleccione usuario que recibe"
                                    style={{
                                        width: '100%', height: '40px',
                                        border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Campos de petición */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 2fr',
                            gap: '20px',
                            marginBottom: '24px'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📋 Petición Asociada
                                </label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <InputText
                                        value={formData.folioPeticion?.toString() || ''}
                                        placeholder="Sin petición"
                                        disabled
                                        style={{
                                            flex: 1, height: '40px', fontSize: '0.95rem',
                                            border: '2px solid #e5e7eb', borderRadius: '8px',
                                            backgroundColor: '#f9fafb'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-search"
                                        onClick={() => openModal('peticiones')}
                                        disabled={appState.mode === 'view'}
                                        size="small"
                                        severity="info"
                                        style={{ height: '40px', borderRadius: '8px' }}
                                        tooltip="Buscar petición"
                                    />
                                    {formData.folioPeticion && (
                                        <Button
                                            icon="pi pi-times"
                                            onClick={() => setFormData(prev => ({ ...prev, folioPeticion: null, tituloPeticion: '' }))}
                                            disabled={appState.mode === 'view'}
                                            size="small"
                                            severity="warning"
                                            style={{ height: '40px', borderRadius: '8px' }}
                                            tooltip="Quitar petición"
                                        />
                                    )}
                                </div>
                            </div>

                            <div>
                                <label style={{
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📝 Título de Petición
                                </label>
                                <InputText
                                    value={formData.tituloPeticion}
                                    placeholder="Título de la petición seleccionada"
                                    disabled
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px', border: '2px solid #e5e7eb',
                                        borderRadius: '8px', backgroundColor: '#f9fafb'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                marginBottom: '8px', color: '#374151'
                            }}>
                                💬 Observaciones
                            </label>
                            <InputTextarea
                                value={formData.observaciones}
                                onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                                disabled={appState.mode === 'view'}
                                rows={3}
                                placeholder="Observaciones generales de la salida..."
                                style={{
                                    width: '100%', fontSize: '0.95rem',
                                    padding: '12px',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '8px',
                                    backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                    </form>
                </div>

                {/* Sección de productos mejorada con control de stock y autocompletado de usuario */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px', padding: '24px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: '20px', paddingBottom: '12px',
                        borderBottom: '2px solid #e5e7eb'
                    }}>
                        <h3 style={{
                            margin: 0, color: '#1f2937', fontSize: '1.3rem', fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            📦 Gestión de Productos
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Badge 
                                value={products.length} 
                                severity={products.length > 0 ? 'success' : 'secondary'}
                                style={{ fontSize: '0.9rem' }}
                            />
                            {appState.loadingStock && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                    color: '#3b82f6', fontSize: '0.85rem'
                                }}>
                                    <i className="pi pi-spin pi-spinner"></i>
                                    <span>Actualizando stock...</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Formulario de productos con validación de stock y autocompletado de usuario */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '20px',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                🔍 Producto <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <InputText
                                    value={productData.nombreProducto}
                                    onChange={(e) => setProductData(prev => ({ ...prev, nombreProducto: e.target.value }))}
                                    disabled
                                    placeholder="Seleccione un producto"
                                    style={{
                                        flex: 1, height: '36px', fontSize: '0.9rem',
                                        border: '2px solid #e5e7eb', borderRadius: '6px',
                                        backgroundColor: '#ffffff'
                                    }}
                                />
                                <Button
                                    icon="pi pi-search"
                                    onClick={() => openModal('productos')}
                                    disabled={appState.mode === 'view'}
                                    size="small"
                                    severity="info"
                                    style={{ height: '36px', borderRadius: '6px' }}
                                    tooltip="Buscar producto"
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                📝 Descripción <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <InputText
                                value={productData.descripcionProducto}
                                disabled
                                placeholder="Descripción del producto"
                                style={{
                                    width: '100%', height: '36px', fontSize: '0.9rem',
                                    border: '2px solid #e5e7eb', borderRadius: '6px',
                                    backgroundColor: '#f9fafb'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                🔢 Cantidad <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <InputText
                                    type="number"
                                    min="1"
                                    max={productData.stockDisponible}
                                    value={productData.cantidadProducto}
                                    onChange={(e) => setProductData(prev => ({ ...prev, cantidadProducto: e.target.value }))}
                                    disabled={appState.mode === 'view'}
                                    placeholder="1"
                                    style={{
                                        width: '100%', height: '36px', fontSize: '0.9rem',
                                        border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : 
                                            parseInt(productData.cantidadProducto) > productData.stockDisponible ? '#ef4444' : '#3b82f6'}`,
                                        borderRadius: '6px',
                                        backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff'
                                    }}
                                />
                                {parseInt(productData.cantidadProducto) > productData.stockDisponible && productData.stockDisponible > 0 && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                        fontSize: '0.75rem', color: '#ef4444', marginTop: '2px'
                                    }}>
                                        ⚠️ Excede stock disponible
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                📊 Stock Disponible
                            </label>
                            <div style={{
                                height: '36px', display: 'flex', alignItems: 'center',
                                padding: '0 12px', border: '2px solid #e5e7eb',
                                borderRadius: '6px', backgroundColor: '#f9fafb'
                            }}>
                                <Badge
                                    value={productData.stockDisponible}
                                    severity={productData.stockDisponible > 10 ? 'success' : 
                                             productData.stockDisponible > 0 ? 'warning' : 'danger'}
                                    style={{ fontSize: '0.8rem' }}
                                />
                                <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#6b7280' }}>
                                    unidades
                                </span>
                            </div>
                        </div>

                        {/* Campo de usuario mejorado con autocompletado */}
                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                👤 Usuario <span style={{ color: '#ef4444' }}>*</span>
                                <span style={{ 
                                    fontSize: '0.75rem', 
                                    color: '#6b7280', 
                                    fontWeight: '400',
                                    marginLeft: '8px'
                                }}>
                                   
                                </span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <InputText
                                    value={productData.nombreUsuario}
                                    onChange={(e) => setProductData(prev => ({ ...prev, nombreUsuario: e.target.value }))}
                                    onKeyDown={handleUsuarioKeyDown}
                                    disabled={appState.mode === 'view'}
                                    placeholder="Código empleado o nombre"
                                    style={{
                                        flex: 1, height: '36px', fontSize: '0.9rem',
                                        border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '6px',
                                        backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff'
                                    }}
                                />
                                <Button
                                    icon="pi pi-search"
                                    onClick={() => openModal('empleados')}
                                    disabled={appState.mode === 'view'}
                                    size="small"
                                    severity="info"
                                    style={{ height: '36px', borderRadius: '6px' }}
                                    tooltip="Buscar empleado"
                                />
                            </div>
                            {/* Indicador de autocompletado */}
                            {productData.nombreUsuario && appState.mode !== 'view' && (
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#059669',
                                    marginTop: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <i className="pi pi-info-circle"></i>
                                    <span>Presiona Enter para buscar por código</span>
                                </div>
                            )}
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                🏢 Sucursal Destino <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <Dropdown
                                value={productData.idSucursalDestino}
                                options={catalogos.sucursales}
                                optionLabel="nombre"
                                optionValue="id"
                                onChange={(e) => setProductData(prev => ({ ...prev, idSucursalDestino: e.value }))}
                                disabled={appState.mode === 'view'}
                                placeholder="Seleccione sucursal"
                                style={{
                                    width: '100%', height: '36px',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '6px'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                🏬 Departamento Destino <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <Dropdown
                                value={productData.cveDepartamentoDestino}
                                options={catalogos.departamentos}
                                optionLabel="nombre"
                                optionValue="cve"
                                onChange={(e) => setProductData(prev => ({ ...prev, cveDepartamentoDestino: e.value }))}
                                disabled={appState.mode === 'view'}
                                placeholder="Seleccione departamento"
                                style={{
                                    width: '100%', height: '36px',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '6px'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                💬 Observaciones
                            </label>
                            <InputText
                                value={productData.observacionProducto}
                                onChange={(e) => setProductData(prev => ({ ...prev, observacionProducto: e.target.value }))}
                                disabled={appState.mode === 'view'}
                                placeholder="Observaciones del producto"
                                style={{
                                    width: '100%', height: '36px', fontSize: '0.9rem',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '6px',
                                    backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                            {appState.editar ? (
                                <Button
                                    label="Actualizar"
                                    icon="pi pi-check"
                                    onClick={actualizarProducto}
                                    disabled={appState.mode === 'view' || productData.stockDisponible === 0}
                                    severity="success"
                                    size="small"
                                    style={{
                                        height: '36px', fontSize: '0.85rem', fontWeight: '600',
                                        borderRadius: '6px', minWidth: '100px'
                                    }}
                                />
                            ) : (
                                <Button
                                    label="Agregar"
                                    icon="pi pi-plus"
                                    onClick={agregarProducto}
                                    disabled={appState.mode === 'view' || productData.stockDisponible === 0}
                                    severity="info"
                                    size="small"
                                    style={{
                                        height: '36px', fontSize: '0.85rem', fontWeight: '600',
                                        borderRadius: '6px', minWidth: '100px'
                                    }}
                                />
                            )}
                            <Button
                                label="Limpiar"
                                icon="pi pi-times"
                                onClick={limpiarCamposProducto}
                                disabled={appState.mode === 'view'}
                                severity="secondary"
                                size="small"
                                style={{
                                    height: '36px', fontSize: '0.85rem', fontWeight: '600',
                                    borderRadius: '6px', minWidth: '80px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Alerta de stock bajo */}
                    {productData.nombreProducto && productData.stockDisponible <= 5 && productData.stockDisponible > 0 && (
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#92400e' }}></i>
                            <span style={{ color: '#92400e', fontWeight: '600' }}>
                                ⚠️ Stock bajo: Solo quedan {productData.stockDisponible} unidades disponibles
                            </span>
                        </div>
                    )}

                    {/* Alerta de sin stock */}
                    {productData.nombreProducto && productData.stockDisponible === 0 && (
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#fee2e2',
                            border: '1px solid #ef4444',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-times-circle" style={{ color: '#dc2626' }}></i>
                            <span style={{ color: '#dc2626', fontWeight: '600' }}>
                                ❌ Sin stock: Este producto no tiene unidades disponibles
                            </span>
                        </div>
                    )}

                    {/* Tabla de productos mejorada */}
                    <ResponsiveProductsTable
                        products={products}
                        catalogos={catalogos}
                        appState={appState}
                        onEditProduct={seleccionarProductoParaEditar}
                        onDeleteProduct={eliminarProducto}
                    />

                    {/* Información adicional de productos con stock */}
                    {products.length > 0 && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #bae6fd',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '8px'
                        }}>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>
                                📦 Total de productos: {products.length}
                            </span>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>
                                🔢 Cantidad total: {products.reduce((sum, p) => sum + p.cantidad, 0)}
                            </span>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>
                                📊 Stock total afectado: {products.reduce((sum, p) => sum + (p.stockDisponible || 0), 0)}
                            </span>
                            {appState.mode !== 'view' && (
                                <span style={{ 
                                    color: '#059669', 
                                    fontWeight: '600',
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic'
                                }}>
                                    ✏️ Modo edición activo
                                </span>
                            )}
                        </div>
                    )}

                    {/* Mensaje cuando no hay productos */}
                    {products.length === 0 && appState.mode !== 'view' && (
                        <div style={{
                            marginTop: '16px',
                            padding: '20px',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <div style={{ 
                                color: '#92400e', 
                                fontWeight: '600',
                                marginBottom: '8px',
                                fontSize: '1rem'
                            }}>
                                📦 No hay productos agregados
                            </div>
                            <div style={{ 
                                color: '#92400e', 
                                fontSize: '0.9rem'
                            }}>
                                Agregue al menos un producto para poder guardar la salida
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer con información del sistema */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px', padding: '16px', marginTop: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '16px',
                        fontSize: '0.85rem',
                        color: '#6b7280'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="pi pi-info-circle"></i>
                            <span>Sistema de Gestión de Inventarios con Control de Stock y Autocompletado</span>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="pi pi-calendar"></i>
                                <span>{new Date().toLocaleDateString('es-ES')}</span>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="pi pi-user"></i>
                                <span>Usuario: {catalogos.usuarios.find(u => u.id === formData.idUsuarioCapturo)?.nombre || 'No identificado'}</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="pi pi-database"></i>
                                <span>Stock en tiempo real</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="pi pi-bolt"></i>
                                <span>Autocompletado activo</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Salidas;